import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Alert, Button, Modal, Spinner} from 'react-bootstrap';
import {Calendar, momentLocalizer, Views} from 'react-big-calendar';
import {FaBan, FaBell, FaCalendarAlt, FaLock, FaMapMarkerAlt, FaUserFriends, FaUsers} from 'react-icons/fa';
import moment from 'moment';
import 'moment/locale/it';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import * as api from '../api';
import {calendarMessages} from '../constants';
import {getDiscountedBookingPrice} from '../util';
import SkyworkPlannerToolbar from './skywork-planner-toolbar';
import {useTranslation} from 'react-i18next';
import {
  compactCapacityLabel,
  isFullAvailableSlot,
  isMultiPlaceSlot,
  isOpenMultiPlaceSlot
} from '../util/booking-slot-capacity';

const localizer = momentLocalizer(moment);

const SLOT_TYPES = {
  individual: {color: '#31BC8C', labelKey: 'BOOKING_SLOT_SINGLE', descriptionKey: 'BOOKING_SLOT_SINGLE_DESCRIPTION'},
  multiple: {color: '#25C75B', labelKey: 'BOOKING_SLOT_PLACES', descriptionKey: 'BOOKING_SLOT_PLACES_DESCRIPTION'},
  group: {color: '#308771', labelKey: 'BOOKING_SLOT_INVITATION', descriptionKey: 'BOOKING_SLOT_INVITATION_DESCRIPTION'},
  booked: {color: '#3598DB', labelKey: 'BOOKED', descriptionKey: 'BOOKING_SLOT_BOOKED_DESCRIPTION'},
  reserved: {color: '#ADB5BD', labelKey: 'BOOKING_SLOT_RESERVED', descriptionKey: 'BOOKING_SLOT_RESERVED_DESCRIPTION'},
  unavailable: {color: '#444444', labelKey: 'BOOKING_SLOT_UNAVAILABLE', descriptionKey: 'BOOKING_SLOT_UNAVAILABLE_DESCRIPTION'}
};

const getSlotType = slot => {
  if (slot.state === 'reserved') return SLOT_TYPES.reserved;
  if (slot.state !== 'available') return SLOT_TYPES.unavailable;
  if (isFullAvailableSlot(slot)) return SLOT_TYPES.booked;
  if (slot.presetPlaces > 1 && slot.minimumPlayers !== null) return SLOT_TYPES.group;
  if (slot.presetPlaces > 1) return SLOT_TYPES.multiple;
  return SLOT_TYPES.individual;
};

const getEventColor = event => {
  if (event.slot.state === 'reserved') return event.slot.color || SLOT_TYPES.reserved.color;
  return event.slotType.color;
};

const sportLabelValue = sport => {
  if (!sport) return '';
  if (typeof sport === 'string' || typeof sport === 'number') return String(sport);
  if (typeof sport === 'object') return sport.name || sport.label || sport.value || sport.sport || sport.type || '';
  return String(sport);
};

const PublicPartnerBookingCalendar = ({
  partnerSlug,
  partnerName,
  affiliations,
  authenticatedPlayer,
  mode,
  onLoginRequired,
  onUseAuthenticatedPlayer,
  onVideoSlotSelected
}) => {
  const {t} = useTranslation();
  const [targetDate, setTargetDate] = useState(new Date());
  const [availability, setAvailability] = useState({fields: [], slots: []});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [view, setView] = useState(Views.DAY);
  const [selectedFieldId, setSelectedFieldId] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;
    const loadAvailability = async () => {
      setLoading(true);
      setError(null);
      const res = await api.getPublicPartnerBookingAvailability(partnerSlug, targetDate, mode);
      if (!active) return;
      if (res.status === 200) {
        setAvailability({
          fields: res.json.fields || [],
          slots: res.json.slots || []
        });
      } else {
        setError(t('BOOKING_AVAILABILITY_LOAD_ERROR'));
      }
      setLoading(false);
    };

    loadAvailability();
    return () => {
      active = false;
    };
  }, [mode, partnerSlug, t, targetDate]);

  const visibleFields = useMemo(() => selectedFieldId
    ? availability.fields.filter(field => String(field.id) === String(selectedFieldId))
    : availability.fields, [availability.fields, selectedFieldId]);

  const selectedField = useMemo(
    () => availability.fields.find(field => String(field.id) === String(selectedFieldId)) || null,
    [availability.fields, selectedFieldId]
  );

  const resources = useMemo(() => visibleFields.map(field => ({
    resourceId: field.id,
    resourceTitle: (
      <button
        type="button"
        className="skywork-planner-field-button"
        onClick={() => {
          setSelectedFieldId(field.id);
          setView(Views.WEEK);
        }}
      >
        <FaMapMarkerAlt aria-hidden="true"/>
        <span className="skywork-planner-field-heading">
          <strong>{field.name}</strong>
          {(field.sport || field.terrain) && <small>{[sportLabelValue(field.sport), field.terrain].filter(Boolean).join(' · ')}</small>}
        </span>
      </button>
    )
  })), [visibleFields]);

  const discount = useCallback(fieldId => {
    let value = 0;
    affiliations
      .filter(affiliation => affiliation.status === 'active')
      .forEach(affiliation => {
        const appliesToField = (affiliation.fields || []).some(field => field.id === fieldId);
        if (appliesToField && affiliation.discount > value) {
          value = affiliation.discount;
        }
      });
    return value;
  }, [affiliations]);

  const getEventTitle = useCallback(slot => {
    if (slot.state === 'reserved') {
      return slot.description || t('BOOKING_SLOT_RESERVED');
    }

    if (slot.state === 'unavailable') {
      return slot.description || t('BOOKING_SLOT_UNAVAILABLE');
    }

    if (isFullAvailableSlot(slot)) {
      return t('BOOKED');
    }

    if (isMultiPlaceSlot(slot)) return t('BOOKING_SLOT_AVAILABLE');

    return slot.description || t('BOOKING_SLOT_AVAILABLE');
  }, [t]);

  const events = useMemo(() => availability.slots.filter(slot => !selectedFieldId || String(slot.fieldId) === String(selectedFieldId)).map(slot => {
    const available = mode === 'booking' && slot.state === 'available' && slot.availablePlaces > 0;
    const videoSelectable = mode === 'calendar' && slot.state === 'reserved' && slot.videoPurchasable;
    const slotType = getSlotType(slot);
    return {
      id: slot.slotId,
      title: getEventTitle(slot),
      start: new Date(slot.startDatetime),
      end: new Date(slot.endDatetime),
      resourceId: slot.fieldId,
      slot,
      available,
      videoSelectable,
      slotType
    };
  }), [availability.slots, getEventTitle, mode, selectedFieldId]);

  const slotStatus = event => {
    if (event.slot.state === 'reserved') return t('BOOKING_SLOT_RESERVED');
    if (event.slot.state === 'unavailable') return t('BOOKING_SLOT_UNAVAILABLE');
    if (isOpenMultiPlaceSlot(event.slot)) return compactCapacityLabel(event.slot, t);
    if (isFullAvailableSlot(event.slot)) return t('BOOKED');
    if (event.slot.minimumPlayers !== null) return t('BOOKING_SLOT_INVITATION');
    return `${t('BOOKING_SLOT_OPEN')} · ${t('BOOKING_SLOT_SINGLE')}`;
  };

  const slotClass = event => {
    if (event.slot.state === 'reserved') return 'reserved';
    if (event.slot.state === 'unavailable') return 'unavailable';
    if (isFullAvailableSlot(event.slot)) return 'booked';
    if (event.slot.minimumPlayers !== null) return 'invitation';
    if (event.slot.presetPlaces > 1) return 'places';
    return 'single';
  };

  const SlotIcon = ({event}) => {
    if (event.slot.state === 'reserved') return <FaLock/>;
    if (event.slot.state === 'unavailable') return <FaBan/>;
    if (isOpenMultiPlaceSlot(event.slot)) return <FaUsers/>;
    if (isFullAvailableSlot(event.slot)) return <FaBell/>;
    if (event.slot.minimumPlayers !== null) return <FaUserFriends/>;
    if (event.slot.presetPlaces > 1) return <FaUsers/>;
    return <FaCalendarAlt/>;
  };

  const CalendarEvent = ({event}) => (
    <div className="booking-calendar-slot-content" aria-label={`${slotStatus(event)}. ${event.title}`}>
      <span className="booking-calendar-slot-status"><SlotIcon event={event}/>{slotStatus(event)}</span>
      <span className="booking-calendar-slot-title">{event.title}</span>
    </div>
  );

  const Toolbar = toolbarProps => (
    <SkyworkPlannerToolbar
      {...toolbarProps}
      view={view}
      fields={availability.fields}
      selectedFieldId={selectedFieldId}
      onFieldChange={fieldId => {
        setSelectedFieldId(fieldId);
        setView(fieldId ? Views.WEEK : Views.DAY);
      }}
      weekDisabled={!selectedFieldId}
    />
  );

  const minTime = new Date();
  minTime.setHours(7, 0, 0, 0);
  const maxTime = new Date();
  maxTime.setHours(23, 58, 59, 999);

  return (
    <section className="public-partner-booking-calendar player-booking-calendar-shell" style={{'--current-time-label': `"${moment(currentTime).format('HH:mm')}"`}}>
      <div className="public-partner-booking-calendar-heading">
        <div>
          <h3>{mode === 'calendar' ? t('PUBLIC_PARTNER_BOOKING_CALENDAR_TITLE_CALENDAR') : t('PUBLIC_PARTNER_BOOKING_CALENDAR_TITLE_BOOKING')}</h3>
          <p>{mode === 'calendar'
            ? t('PUBLIC_PARTNER_BOOKING_CALENDAR_BODY_CALENDAR', {partnerName})
            : t('PUBLIC_PARTNER_BOOKING_CALENDAR_BODY_BOOKING', {partnerName})}</p>
        </div>
      </div>
      <div className="booking-calendar-legend-title">{t('BOOKING_CALENDAR_LEGEND')}</div>
      <div className="booking-calendar-legend public-partner-booking-legend">
        {Object.entries(SLOT_TYPES).map(([key, type]) => (
          <span key={key}>
            <i style={{backgroundColor: type.color}}/>
            <span>
              <strong>{t(type.labelKey)}</strong>
              <small>{t(type.descriptionKey)}</small>
            </span>
          </span>
        ))}
      </div>
      <div className="public-partner-booking-calendar-context" aria-live="polite">
        <span>{selectedField ? t('PUBLIC_PARTNER_BOOKING_CALENDAR_SELECTED_FIELD') : t('PUBLIC_PARTNER_BOOKING_CALENDAR_ALL_FIELDS')}</span>
        <strong>
          {selectedField
            ? [selectedField.name, selectedField.sport, selectedField.terrain].filter(Boolean).join(' · ')
            : partnerName}
        </strong>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && (
        <div className="public-partner-booking-loading">
          <Spinner animation="border" role="status"/>
        </div>
      )}
      {!loading && availability.fields.length === 0 && (
        <Alert variant="info">{mode === 'calendar' ? t('PUBLIC_PARTNER_BOOKING_CALENDAR_NO_FIELDS_CALENDAR') : t('PUBLIC_PARTNER_BOOKING_CALENDAR_NO_FIELDS_BOOKING')}</Alert>
      )}
      {!loading && availability.fields.length > 0 && (
        <Calendar
          localizer={localizer}
          getNow={() => currentTime}
          culture="it"
          defaultView={Views.DAY}
          view={view}
          onView={setView}
          views={['day', 'week']}
          date={targetDate}
          min={minTime}
          max={maxTime}
          events={events}
          resources={resources}
          resourceIdAccessor="resourceId"
          resourceTitleAccessor="resourceTitle"
          step={15}
          timeslots={4}
          messages={calendarMessages}
          onNavigate={setTargetDate}
          onSelectEvent={event => {
            if (event.available) {
              setSelectedSlot(event.slot);
              return;
            }
            if (event.videoSelectable && typeof onVideoSlotSelected === 'function') {
              onVideoSlotSelected(event.slot);
            }
          }}
          components={{event: CalendarEvent, toolbar: Toolbar}}
          eventPropGetter={event => ({
            className: `booking-calendar-slot booking-calendar-slot--${slotClass(event)}`,
            style: {
              backgroundColor: getEventColor(event),
              borderColor: '#00152F',
              borderRadius: '5px',
              color: '#fff',
              display: 'block',
              opacity: 1,
              cursor: event.available || event.videoSelectable ? 'pointer' : 'not-allowed'
            }
          })}
        />
      )}

      <Modal show={Boolean(selectedSlot)} onHide={() => setSelectedSlot(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{authenticatedPlayer ? t('CONFIRM_ACCOUNT') : t('LOGIN_TO_BOOK')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {authenticatedPlayer ? (
            <span>
              {t('ALREADY_LOGGED_IN_AS')} <strong>{authenticatedPlayer}</strong>. {t('CONTINUE_OR_USE_ANOTHER_ACCOUNT')}
            </span>
          ) : (
            <span>
              {t('LOGIN_TO_CONFIRM_PUBLIC_SLOT')}
            </span>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setSelectedSlot(null)}>{t('CANCEL')}</Button>
          {authenticatedPlayer && (
            <Button
              variant="outline-primary"
              onClick={() => {
                onUseAuthenticatedPlayer(selectedSlot);
                setSelectedSlot(null);
              }}
            >
              {t('CONTINUE_AS_USER', {name: authenticatedPlayer})}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => {
              onLoginRequired(selectedSlot);
              setSelectedSlot(null);
            }}
          >
            {authenticatedPlayer ? t('LOGIN_REGISTER_ANOTHER_ACCOUNT') : t('LOGIN_OR_REGISTER')}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

PublicPartnerBookingCalendar.propTypes = {
  affiliations: PropTypes.array,
  authenticatedPlayer: PropTypes.string,
  mode: PropTypes.oneOf(['booking', 'calendar']),
  onLoginRequired: PropTypes.func.isRequired,
  onUseAuthenticatedPlayer: PropTypes.func.isRequired,
  onVideoSlotSelected: PropTypes.func,
  partnerName: PropTypes.string.isRequired,
  partnerSlug: PropTypes.string.isRequired
};

PublicPartnerBookingCalendar.defaultProps = {
  affiliations: [],
  authenticatedPlayer: null,
  mode: 'booking',
  onVideoSlotSelected: null
};

export default PublicPartnerBookingCalendar;
